import type { IconProps } from '@shopify/polaris';

import { Box, Icon, InlineStack, Text } from '@shopify/polaris';
import { useEffect, useRef } from 'react';
import { GDiv } from '~/presentation/ui';
import './GTabs.scss';

export interface GTabsItem<T extends string = string> {
  label: string;
  value: T;
  icon: IconProps['source'];
}

export interface GTabsProps<T extends string = string> {
  tabs: GTabsItem<T>[];
  selected: T;
  onChange: (value: T) => void;
}

interface GTabsItemViewProps<T extends string> {
  item: GTabsItem<T>;
  isActive: boolean;
  isFirst: boolean;
  isLast: boolean;
  onClick: () => void;
}

const GTabsItemView = <T extends string>({
  item,
  isActive,
  isFirst,
  isLast,
  onClick,
}: GTabsItemViewProps<T>) => {
  const className = [
    'GTabsItem',
    isActive && 'GTabsItem--active',
    isActive && isFirst && 'GTabsItem--first',
    isActive && isLast && 'GTabsItem--last',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <GDiv
      onClick={onClick}
      role="tab"
      aria-selected={isActive}
      data-js-tab-active={isActive}
      tabIndex={isActive ? 0 : -1}
      className={className}
    >
      <InlineStack gap="100" align="center" blockAlign="center" wrap={false}>
        <Box minWidth="16px">
          <Icon source={item.icon} />
        </Box>
        <Text as="span" variant="bodyMd" fontWeight="medium" truncate>
          {item.label}
        </Text>
      </InlineStack>
    </GDiv>
  );
};

export const GTabs = <T extends string>({ tabs, selected, onChange }: GTabsProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Tabs scroll horizontally on narrow screens, keep the selected one in view
  useEffect(() => {
    const container = containerRef.current;
    const activeTab = container?.querySelector<HTMLElement>('[data-js-tab-active="true"]');
    if (!container || !activeTab) return;

    container.scrollTo({
      left: activeTab.offsetLeft - (container.clientWidth - activeTab.offsetWidth) / 2,
      behavior: 'smooth',
    });
  }, [selected]);

  return (
    <div ref={containerRef} className="GTabs">
      <InlineStack wrap={false}>
        {tabs.map((tab, index) => (
          <GTabsItemView
            key={tab.value}
            item={tab}
            isActive={tab.value === selected}
            isFirst={index === 0}
            isLast={index === tabs.length - 1}
            onClick={() => onChange(tab.value)}
          />
        ))}
      </InlineStack>
    </div>
  );
};
